import { Outlet, createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import z from 'zod';

import { RequireEntitlements } from '@/components/Auth/RequireEntitlements';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { SettingsPageLayout } from '@/components/Settings/SettingsPageLayout';
import { AdminSources } from '@/components/Sources/AdminSources';
import { sourceVisibilityValues } from '@/components/Sources/sourceVisibilityItems';
import { useIsExactRouteMatch } from '@/hooks/useIsExactRouteMatch';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';
import { requiredSortingSearchParam } from '@/utils/tableSorting';

const sourcesSearchSchema = z.object({
  sPageSize: fallback(z.number(), DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  sPageIndex: fallback(z.number(), 0).default(0),
  sSearch: searchParam().optional(),
  sKind: z.enum(['all', 'CODEBASE', 'FILE', 'PAGE']).optional(),
  sVisibility: z.enum(sourceVisibilityValues).optional(),
  sSorting: requiredSortingSearchParam([{ id: 'display_name', desc: false }]),
});

export const Route = createFileRoute('/$organizationName/settings/_access/sources')({
  component: SourcesLayout,
  validateSearch: zodValidator(sourcesSearchSchema),
});

function SourcesLayout() {
  const { organizationDisplayName } = useUser();
  const isExactMatch = useIsExactRouteMatch(Route.id);

  useEffect(() => {
    if (isExactMatch) {
      document.title = `${organizationDisplayName || 'Driver'} - Sources`;
    }
  }, [organizationDisplayName, isExactMatch]);

  return (
    <RequireEntitlements admins={['isSuperAdmin', 'isSourceAdmin']}>
      <SettingsPageLayout fullWidthWithPadding hideSeparator>
        <AdminSources />
        <Outlet />
      </SettingsPageLayout>
    </RequireEntitlements>
  );
}
